import { useEffect, useState, useRef } from "react";
import api, { fileUrl } from "@/lib/api";
import { uploadFile } from "@/lib/upload";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, ExternalLink, Upload, AlertTriangle, Wand2, Link2, GripVertical } from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, sortableKeyboardCoordinates, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const BADGE = {
  ready: ["Ready", "text-emerald-600 bg-emerald-50 border-emerald-200"],
  needs_setup: ["Needs setup", "text-amber-600 bg-amber-50 border-amber-200"],
  invalid: ["Invalid config", "text-destructive bg-destructive/10 border-destructive/30"],
  loading: ["Checking…", "text-muted-foreground bg-secondary border-border"],
  disabled: ["Disabled", "text-muted-foreground bg-secondary border-border"],
};

const SortableCard = ({ pm, def, mode, preview, readiness, setPm, uploadQr, qrRefs, copy }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: pm.type });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 20 : "auto" };
  const badge = BADGE[readiness.state] || BADGE.loading;
  return (
    <div ref={setNodeRef} style={style} className={`border border-border rounded-lg p-4 space-y-3 bg-card ${isDragging ? "shadow-lg" : ""}`} data-testid={`pm-card-${pm.type}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <button type="button" {...attributes} {...listeners} aria-label={`Drag to reorder ${def.name || pm.label}`}
            className="touch-none cursor-grab active:cursor-grabbing p-1 -ml-1 text-muted-foreground hover:text-foreground rounded"
            data-testid={`pm-drag-handle-${pm.type}`}>
            <GripVertical className="h-5 w-5" />
          </button>
          <span className="font-medium truncate">{def.name || pm.label}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full border shrink-0 ${badge[1]}`} data-testid={`pm-readiness-${pm.type}`}>{badge[0]}</span>
        </div>
        <Switch checked={pm.enabled} onCheckedChange={(v) => setPm(pm.type, "enabled", v)} data-testid={`payment-toggle-${pm.type}`} />
      </div>
      {pm.enabled && (readiness.missing || []).length > 0 && (
        <p className="text-xs text-amber-600 flex items-center gap-1" data-testid={`pm-missing-${pm.type}`}><AlertTriangle className="h-3 w-3" /> Missing: {readiness.missing.join(", ")}</p>
      )}

      {pm.enabled && pm.type !== "cod" && (
        <div className="space-y-3">
          {def.supports_custom && (
            <div className="flex gap-2">
              <button type="button" onClick={() => setPm(pm.type, "mode", "auto")} className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm ${mode === "auto" ? "border-primary bg-primary/5 text-primary font-medium" : "border-border text-muted-foreground"}`} data-testid={`pm-mode-auto-${pm.type}`}><Wand2 className="h-3.5 w-3.5" /> Automatic link</button>
              <button type="button" onClick={() => setPm(pm.type, "mode", "custom")} className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm ${mode === "custom" ? "border-primary bg-primary/5 text-primary font-medium" : "border-border text-muted-foreground"}`} data-testid={`pm-mode-custom-${pm.type}`}><Link2 className="h-3.5 w-3.5" /> Custom URL</button>
            </div>
          )}

          {mode === "custom" ? (
            <div className="space-y-1">
              <Label className="text-xs">Custom payment URL</Label>
              <Input value={pm.custom_url || ""} onChange={(e) => setPm(pm.type, "custom_url", e.target.value)} placeholder="https://... (supports {ORDER_TOTAL}, {ORDER_NUMBER}, {NOTE})" className="h-10" data-testid={`pm-custom-url-${pm.type}`} />
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {(def.required_fields || []).map((fld) => (
                <div key={fld.key} className="space-y-1">
                  <Label className="text-xs">{fld.label}</Label>
                  <Input value={pm[fld.key] || ""} onChange={(e) => setPm(pm.type, fld.key, e.target.value)} placeholder={fld.hint || ""} className="h-10" data-testid={`pm-field-${pm.type}-${fld.key}`} />
                </div>
              ))}
            </div>
          )}

          {preview && (
            <div className="rounded-lg bg-secondary/40 border border-border p-3 space-y-2" data-testid={`pm-preview-${pm.type}`}>
              <p className="text-xs font-medium text-muted-foreground">Live preview (Order $25.00, #12345)</p>
              {preview.payment_url ? (
                <>
                  <p className="text-sm font-mono break-all text-primary">{preview.payment_url}</p>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" className="rounded-lg h-8" onClick={() => copy(preview.payment_url)} data-testid={`pm-copy-${pm.type}`}><Copy className="h-3.5 w-3.5 mr-1" /> Copy</Button>
                    <a href={preview.payment_url} target="_blank" rel="noreferrer"><Button type="button" size="sm" variant="outline" className="rounded-lg h-8" data-testid={`pm-test-${pm.type}`}><ExternalLink className="h-3.5 w-3.5 mr-1" /> Test link</Button></a>
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">This provider uses recipient details + instructions (no auto link).</p>
              )}
              {(preview.warnings || []).map((w, wi) => (
                <p key={wi} className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {w}</p>
              ))}
            </div>
          )}

          {def.supports_qr && (
            <div className="flex items-center gap-3">
              {pm.qr_code_url && <img src={fileUrl(pm.qr_code_url)} alt="QR" className="h-14 w-14 rounded border border-border object-contain bg-white" />}
              <Button type="button" size="sm" variant="outline" className="rounded-lg" onClick={() => qrRefs.current[pm.type]?.click()} data-testid={`pm-qr-upload-${pm.type}`}><Upload className="h-3.5 w-3.5 mr-1" /> {pm.qr_code_url ? "Replace" : "Upload"} QR</Button>
              <input ref={(el) => (qrRefs.current[pm.type] = el)} type="file" accept="image/*" hidden onChange={(e) => uploadQr(pm.type, e)} />
            </div>
          )}

          <label className="flex items-center gap-2 text-sm"><Switch checked={pm.require_proof !== false} onCheckedChange={(v) => setPm(pm.type, "require_proof", v)} data-testid={`payment-require-proof-${pm.type}`} /> Require payment proof</label>
          <Textarea value={pm.instructions || ""} onChange={(e) => setPm(pm.type, "instructions", e.target.value)} placeholder="Customer instructions (optional)" rows={2} data-testid={`payment-instructions-${pm.type}`} />
        </div>
      )}

      {pm.enabled && pm.type === "cod" && (
        <Textarea value={pm.instructions || ""} onChange={(e) => setPm(pm.type, "instructions", e.target.value)} placeholder="Cash on delivery instructions" rows={2} data-testid="payment-instructions-cod" />
      )}
    </div>
  );
};

export const PaymentMethodsEditor = ({ methods, onChange, focusMethodId }) => {
  const [providers, setProviders] = useState({});
  const [previews, setPreviews] = useState({});
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("drelik_pm_banner_dismissed") === "1");
  const qrRefs = useRef({});

  useEffect(() => {
    if (!focusMethodId || !Object.keys(providers).length) return;
    const t = setTimeout(() => {
      const card = document.querySelector(`[data-testid="pm-card-${focusMethodId}"]`);
      if (!card) return;
      card.scrollIntoView({ behavior: "smooth", block: "center" });
      const field = card.querySelector("input:not([type=file]), textarea");
      field?.focus({ preventScroll: true });
    }, 500);
    return () => clearTimeout(t);
  }, [focusMethodId, providers]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    api.get("/store/payment-providers").then((r) => {
      const m = {}; r.data.forEach((p) => { m[p.key] = p; }); setProviders(m);
    });
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      const next = {};
      for (const pm of methods) {
        const def = providers[pm.type];
        if (!def || !pm.enabled || pm.type === "cod") continue;
        try {
          const { data } = await api.post("/store/payment-methods/preview", {
            provider: pm.type, fields: pm, mode: pm.mode || "auto", custom_url: pm.custom_url || "",
          });
          next[pm.type] = data;
        } catch { /* ignore */ }
      }
      setPreviews(next);
    }, 400);
    return () => clearTimeout(t);
  }, [methods, providers]);

  const setPm = (type, f, v) => onChange(methods.map((p) => p.type === type ? { ...p, [f]: v } : p));

  const uploadQr = async (type, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const path = await uploadFile(f); setPm(type, "qr_code_url", path); toast.success("QR uploaded"); }
    catch { toast.error("Upload failed"); }
  };

  const copy = (url) => { navigator.clipboard?.writeText(url); toast.success("Link copied"); };

  const readinessOf = (pm) => {
    if (!pm.enabled) return { state: "disabled", missing: [] };
    if (pm.type === "cod") return { state: "ready", missing: [] };
    return previews[pm.type]?.readiness || { state: "loading", missing: [] };
  };
  const notReady = methods.filter((m) => m.enabled && !["ready", "loading"].includes(readinessOf(m).state));
  const showBanner = !dismissed && notReady.length > 0;
  const reviewFirst = () => document.querySelector(`[data-testid="pm-card-${notReady[0].type}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  const dismiss = () => { sessionStorage.setItem("drelik_pm_banner_dismissed", "1"); setDismissed(true); };

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = methods.findIndex((m) => m.type === active.id);
    const newIndex = methods.findIndex((m) => m.type === over.id);
    const reordered = arrayMove(methods, oldIndex, newIndex).map((m, idx) => ({ ...m, display_order: idx + 1 }));
    onChange(reordered);
    const moved = providers[active.id]?.name || active.id;
    toast.success(`Moved ${moved} to position ${newIndex + 1}`);
  };

  const ids = methods.map((m) => m.type);

  return (
    <div className="space-y-4">
      {showBanner && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="pm-readiness-banner">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-amber-800 text-sm">Payment setup needs attention</p>
            <p className="text-sm text-amber-700">{notReady.length} enabled payment method{notReady.length > 1 ? "s are" : " is"} missing required information and will not appear at checkout.</p>
          </div>
          <div className="flex gap-2">
            <Button type="button" size="sm" className="rounded-lg" onClick={reviewFirst} data-testid="pm-banner-review">Review methods</Button>
            <Button type="button" size="sm" variant="ghost" className="rounded-lg" onClick={dismiss} data-testid="pm-banner-dismiss">Dismiss for session</Button>
          </div>
        </div>
      )}
      <p className="text-sm text-muted-foreground">Drag the <GripVertical className="h-3.5 w-3.5 inline -mt-0.5" /> handle to set the order customers see at checkout. Add your payment identity and Drelik builds the links automatically.</p>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="space-y-4">
            {methods.map((pm) => (
              <SortableCard key={pm.type} pm={pm} def={providers[pm.type] || {}} mode={pm.mode || "auto"}
                preview={previews[pm.type]} readiness={readinessOf(pm)}
                setPm={setPm} uploadQr={uploadQr} qrRefs={qrRefs} copy={copy} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default PaymentMethodsEditor;
